import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { apiFetch } from '../api';
import Modal from '../components/Modal';
import { useConfirm } from '../components/ConfirmDialog';
import { formatCost } from '../components/charts/format';
import {
  Badge, EmptyState, Field, Icon, PageHeader, SearchInput, Switch, TableSkeleton, Tabs,
} from '../components/ui';

/* ── Formats ─────────────────────────────────────────────────────
 * Loaded from GET /api/llm-models/formats, which is shared/llm_formats.py: the registry
 * proxy-llm dispatches by. The form therefore cannot offer a format the proxy has no
 * provider for, nor misshow one it has (until 2026-09-02 a hardcoded list here rendered
 * Claude Sonnet as "OpenAI / vLLM" because its spelling was missing).
 *
 * Reasoning belongs to the MODEL, not the format: `supports_reasoning`, and
 * `settings.reasoning` for how the proxy steers it. The old `openai-thinking` format is
 * an `openai` model whose reasoning mode is the vLLM thinking switch. */
const BADGES = {
  'bedrock-converse': { tone: 'violet', label: 'Bedrock' },
  anthropic: { tone: 'accent', label: 'Anthropic' },
  'azure-openai': { tone: 'outline', label: 'Azure' },
  openai: { tone: 'neutral', label: 'OpenAI' },
};

// A value the registry does not know still shows as itself, flagged, never as another.
const providerBadge = (apiType) => {
  if (!apiType) return null;
  const b = BADGES[apiType];
  return b ? <Badge tone={b.tone}>{b.label}</Badge> : <Badge tone="warn">{apiType}</Badge>;
};

// The settings each reasoning mode takes, beyond the mode itself.
const MODE_FIELDS = {
  chat_template: ['min_output_tokens'],
  no_thinking: [],
  template_effort: ['effort', 'min_output_tokens'],
  effort: ['effort'],
  extended_thinking: ['budget_tokens'],
  model_default: [],
};

const EMPTY_FORM = {
  model_key: '', ui_name: '', model_id: '',
  api_type: 'openai',
  base_url: '', namespace_id: '', api_key: '',
  context_window: '', max_tokens: '', supports_reasoning: false,
  reasoning: '', effort: '', budget_tokens: '', min_output_tokens: '', api_version: '',
  send_stream_options: true,
  available_as_bg: true, active: true, sort_order: 0,
  price_input_per_m: '', price_output_per_m: '',
  price_cache_write_per_m: '', price_cache_read_per_m: '',
  price_cache_write_l2_per_m: '',
};

// [key, label, hint, placeholder]. Cache writes are priced by how long the entry is kept:
// long cache (1 hour, `price_cache_write_per_m`) and short cache (5 minutes,
// `price_cache_write_l2_per_m`), the names the Dashboard uses too. The hints say nothing
// about any one format, because the fields show for every format.
const PRICE_FIELDS = [
  ['price_input_per_m', 'Input'],
  ['price_output_per_m', 'Output'],
  ['price_cache_read_per_m', 'Cache read'],
  ['price_cache_write_per_m', 'Long cache write', 'Entries the cache keeps for 1 hour.'],
  ['price_cache_write_l2_per_m', 'Short cache write',
    'Entries the cache keeps for 5 minutes. Blank means the long cache write price.',
    'Same as long'],
];

const DEFAULT_TEST_PROMPT = 'Say hello in one sentence.';

const STATE_FILTERS = {
  all: () => true,
  active: (m) => !!m.active,
  inactive: (m) => !m.active,
};

const DASH = <span className="text-faint">—</span>;

// "200k" and "1M" rather than 200000: context windows are round figures, and the exact
// value is one click away in the editor.
const formatTokens = (n) => {
  if (!n) return null;
  if (n >= 1000000) return `${+(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${Math.round(n / 1000)}k`;
  return String(n);
};

const PRICE = new Intl.NumberFormat(undefined, {
  style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 4,
});

// Zero stays visible but faint: Tiers treats a 0 / 0 model as a free model, so it is a
// real value, not a missing one.
const formatPrice = (v) => {
  const n = Number(v) || 0;
  return n ? PRICE.format(n) : <span className="text-faint">{PRICE.format(0)}</span>;
};

const formatDate = (iso) => (iso
  ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  : null);

// The scheme is noise in a narrow column; the full URL is in the tooltip and the editor.
const shortUrl = (url) => (url || '').replace(/^https?:\/\//, '');

// The levels a reasoning mode takes: its own (GLM's low / high / max) or the format's.
const effortsFor = (format, mode) => (format?.reasoning || []).find((m) => m.value === mode)?.efforts
  || format?.efforts || null;

const modelMatches = (m, q) => [m.ui_name, m.model_key, m.model_id, m.api_type, m.base_url, m.namespace_id]
  .some((v) => v && String(v).toLowerCase().includes(q));

/* ── Test result ─────────────────────────────────────────────────── */
// The Test runs through proxy-llm (backend services/model_test.py), as an agent's call with
// the row's settings, and is billed to nobody: the answer and its reasoning, what proxy-llm
// sent upstream, and what it would have billed.
const CALL_MODES = [
  { value: 'conversation', label: 'Conversation' },
  { value: 'compaction', label: 'Compaction' },
];
const CALL_MODE_HINTS = {
  conversation: 'As an agent turn: streamed, up to the output limit of the model.',
  compaction: 'As an agent summarising its history: not streamed, 2048 tokens, internal work.',
};

const JSON_BLOCK = { maxHeight: 240, overflowY: 'auto' };
const asJson = (v) => (typeof v === 'string' ? v : JSON.stringify(v, null, 2));

function TestResult({ result: r }) {
  const ok = !!r.success;
  // A 4xx from the test route itself ("Model not found") carries only `detail`.
  // It used to render as a bare "Failed" with an empty raw view, hiding the reason.
  const raw = r.raw_response !== undefined ? r.raw_response : r;
  const sent = r.retry_request
    ? { request: r.upstream_request, empty_reply_retry: r.retry_request }
    : r.upstream_request;
  return (
    <div className="stack gap-3">
      <div className={`notice ${ok ? 'notice-ok' : 'notice-danger'} mb-0`}>
        <Icon name={ok ? 'checkCircle' : 'xCircle'} />
        <div className="notice-body">
          <div className="row-wrap gap-3">
            <span className="notice-title">{ok ? 'Success' : 'Failed'}</span>
            {r.status_code != null && <span className="num">HTTP {r.status_code}</span>}
            {r.elapsed_seconds != null && <span className="num">{r.elapsed_seconds}s</span>}
            {r.ttft_ms != null && <span className="num">first token {r.ttft_ms} ms</span>}
            {r.input_tokens != null && (
              <span className="num">{r.input_tokens} in / {r.output_tokens} out</span>
            )}
            {r.reasoning_tokens != null && <span className="num">{r.reasoning_tokens} reasoning</span>}
            {r.finish_reason && <span className="mono text-sm">{r.finish_reason}</span>}
          </div>
          {r.detail && (
            <div className="mt-1">{typeof r.detail === 'string' ? r.detail : JSON.stringify(r.detail)}</div>
          )}
          {r.through === 'proxy-llm' && (
            <div className="mt-1 text-sm text-muted">
              Through proxy-llm, as an agent call. Not billed
              {r.would_cost_usd != null ? `: it would have cost ${formatCost(r.would_cost_usd)}` : ''}.
            </div>
          )}
        </div>
      </div>
      {r.text && <div className="card-inset pre-wrap">{r.text}</div>}
      {r.reasoning && (
        <details>
          <summary className="text-sm text-muted">Reasoning</summary>
          <div className="card-inset pre-wrap mt-2" style={JSON_BLOCK}>{r.reasoning}</div>
        </details>
      )}
      {sent && (
        <details>
          <summary className="text-sm text-muted">Sent upstream by proxy-llm</summary>
          <pre className="code-block mt-2" style={JSON_BLOCK}>{asJson(sent)}</pre>
        </details>
      )}
      {Array.isArray(r.would_bill) && r.would_bill.length > 0 && (
        <details>
          <summary className="text-sm text-muted">What would have been billed</summary>
          <pre className="code-block mt-2" style={JSON_BLOCK}>{asJson(r.would_bill)}</pre>
        </details>
      )}
      <details>
        <summary className="text-sm text-muted">Raw response</summary>
        <pre className="code-block mt-2" style={JSON_BLOCK}>{asJson(raw)}</pre>
      </details>
    </div>
  );
}

/* ── One on/off flag in the editor ──────────────────────────────── */
function SwitchRow({ id, title, description, checked, onChange }) {
  return (
    <div className="setting-row">
      <div className="min-w-0">
        <label className="setting-row-title" htmlFor={id}>{title}</label>
        {description && <div className="setting-row-desc">{description}</div>}
      </div>
      <Switch id={id} checked={checked} onChange={onChange} />
    </div>
  );
}

/* ── Main Models Page ────────────────────────────────────────── */
function Models() {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testStates, setTestStates] = useState({});
  const [testKey, setTestKey] = useState(null);
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [confirm, confirmDialog] = useConfirm();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [registry, setRegistry] = useState(null);

  /* ── Data fetching ─────────────────────────────────────────── */
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [resp, fmt] = await Promise.all([
          apiFetch('/api/llm-models'), apiFetch('/api/llm-models/formats'),
        ]);
        if (resp.ok) setModels(await resp.json());
        else setLoadError(true);
        if (fmt.ok) setRegistry(await fmt.json());
      } catch (e) {
        console.error('Fetch error:', e);
        setLoadError(true);
      } finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  const reloadModels = async () => {
    try {
      const resp = await apiFetch('/api/llm-models');
      if (resp.ok) {
        setModels(await resp.json());
        setLoadError(false);
      }
    } catch (e) { console.error(e); }
  };

  // After a failed first load. The skeleton coming back is the feedback that it tried.
  const retryLoad = async () => {
    setLoading(true);
    await reloadModels();
    setLoading(false);
  };

  /* ── Model CRUD ────────────────────────────────────────────── */
  const update = (key, value) => setFormData((f) => ({ ...f, [key]: value }));

  const resetForm = () => { setFormData(EMPTY_FORM); setEditingKey(null); setShowForm(false); };

  const openCreate = () => { setFormData(EMPTY_FORM); setEditingKey(null); setShowForm(true); };

  const handleEdit = (model) => {
    setFormData({
      model_key: model.model_key,
      ui_name: model.ui_name,
      model_id: model.model_id,
      api_type: model.api_type || 'openai',
      base_url: model.base_url || '',
      namespace_id: model.namespace_id || '',
      api_key: '',  // write-only: empty draft leaves existing key untouched
      _api_key_display: model.api_key_display || '',
      _api_key_set: !!model.api_key_set,
      _api_key_updated_at: model.api_key_updated_at || '',
      context_window: model.context_window || '',
      max_tokens: model.max_tokens || '',
      supports_reasoning: model.supports_reasoning || false,
      reasoning: model.settings?.reasoning || '',
      effort: model.settings?.effort || '',
      budget_tokens: model.settings?.budget_tokens ?? '',
      min_output_tokens: model.settings?.min_output_tokens ?? '',
      api_version: model.settings?.api_version || '',
      send_stream_options: model.settings?.send_stream_options !== false,
      available_as_bg: model.available_as_bg ?? true,
      active: model.active ?? true,
      sort_order: model.sort_order || 0,
      price_input_per_m: model.price_input_per_m || '',
      price_output_per_m: model.price_output_per_m || '',
      price_cache_write_per_m: model.price_cache_write_per_m || '',
      price_cache_read_per_m: model.price_cache_read_per_m || '',
      // `??`, not `||`: 0 is a real short cache price, blank means "same as long".
      price_cache_write_l2_per_m: model.price_cache_write_l2_per_m ?? '',
    });
    setEditingKey(model.model_key);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Strip helper-only display fields before posting
    const {
      _api_key_display, _api_key_set, _api_key_updated_at, api_key,
      reasoning, effort, budget_tokens, min_output_tokens, api_version, send_stream_options,
      ...rest
    } = formData;
    const settings = {};
    const takes = new Set(currentFormat?.settings || []);
    if (rest.supports_reasoning && reasoning) {
      settings.reasoning = reasoning;
      const values = { effort, budget_tokens, min_output_tokens };
      const allowed = effortsFor(currentFormat, reasoning) || [];
      (MODE_FIELDS[reasoning] || []).forEach((k) => {
        if (!takes.has(k) || values[k] === '' || values[k] == null) return;
        // A level left from another mode is not this mode's: the mode's default applies.
        if (k === 'effort' && !allowed.includes(values[k])) return;
        settings[k] = k === 'effort' ? values[k] : parseInt(values[k], 10);
      });
    }
    if (takes.has('api_version') && api_version.trim()) settings.api_version = api_version.trim();
    // Stored only when off: the API replaces the row's settings with these, so a save from
    // the page must carry it, and a row that says nothing asks as before.
    if (takes.has('send_stream_options') && send_stream_options === false) {
      settings.send_stream_options = false;
    }
    const payload = {
      ...rest,
      settings,
      context_window: rest.context_window ? parseInt(rest.context_window, 10) : null,
      max_tokens: rest.max_tokens ? parseInt(rest.max_tokens, 10) : null,
      sort_order: parseInt(rest.sort_order, 10) || 0,
      price_input_per_m: rest.price_input_per_m ? parseFloat(rest.price_input_per_m) : 0,
      price_output_per_m: rest.price_output_per_m ? parseFloat(rest.price_output_per_m) : 0,
      price_cache_write_per_m: rest.price_cache_write_per_m ? parseFloat(rest.price_cache_write_per_m) : 0,
      price_cache_read_per_m: rest.price_cache_read_per_m ? parseFloat(rest.price_cache_read_per_m) : 0,
      // Sent as null when blank, which the API applies: "same as long".
      price_cache_write_l2_per_m: rest.price_cache_write_l2_per_m === '' || rest.price_cache_write_l2_per_m == null
        ? null : parseFloat(rest.price_cache_write_l2_per_m),
      base_url: rest.base_url || null,
      namespace_id: rest.namespace_id || null,
    };
    // Only include api_key when the user actually typed one (write-only).
    if (api_key && api_key.trim()) payload.api_key = api_key.trim();
    setSaving(true);
    try {
      let resp;
      if (editingKey) {
        resp = await apiFetch(`/api/llm-models/${editingKey}`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        resp = await apiFetch('/api/llm-models', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      if (resp.ok) {
        toast.success(editingKey ? 'Model updated' : 'Model created');
        resetForm();
        reloadModels();
      } else {
        const err = await resp.json();
        toast.error(err.detail || 'Failed');
      }
    } catch { toast.error('Failed to save model'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (modelKey) => {
    if (!await confirm({
      title: `Delete model ${modelKey}?`,
      body: (
        <p>
          A model a tier or the Tools page still uses cannot be deleted or switched off:
          point them at another model first.
        </p>
      ),
      confirmLabel: 'Delete model',
    })) return;
    try {
      const resp = await apiFetch(`/api/llm-models/${modelKey}`, { method: 'DELETE' });
      if (resp.ok) { toast.success('Deleted'); reloadModels(); }
      else { const err = await resp.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Failed to delete'); }
  };

  /* ── Model testing ─────────────────────────────────────────── */
  // Kept per model, so closing the test dialog and opening it again shows the last run,
  // and a run still in flight lands on the right model.
  const getTestState = (key) => testStates[key]
    || { prompt: DEFAULT_TEST_PROMPT, mode: 'conversation', loading: false, result: null };
  const setTestPrompt = (key, prompt) => setTestStates(prev => ({ ...prev, [key]: { ...getTestState(key), prompt } }));
  const setTestMode = (key, mode) => setTestStates(prev => ({ ...prev, [key]: { ...getTestState(key), mode } }));

  const runTest = async (modelKey) => {
    const state = getTestState(modelKey);
    setTestStates(prev => ({ ...prev, [modelKey]: { ...state, loading: true, result: null } }));
    try {
      const resp = await apiFetch(`/api/llm-models/${modelKey}/test`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: state.prompt, mode: state.mode || 'conversation' }),
      });
      const data = await resp.json();
      setTestStates(prev => ({ ...prev, [modelKey]: { ...prev[modelKey], loading: false, result: data } }));
    } catch (err) {
      setTestStates(prev => ({
        ...prev,
        [modelKey]: { ...prev[modelKey], loading: false, result: { success: false, text: null, raw_response: err.message, elapsed_seconds: 0, status_code: 0 } }
      }));
    }
  };

  /* ── Derived data ──────────────────────────────────────────── */
  // The list is not paginated server-side (GET /api/llm-models returns every row), so
  // search and the state filter run here.
  const q = search.trim().toLowerCase();
  const activeCount = models.filter(STATE_FILTERS.active).length;
  const visible = models.filter((m) => STATE_FILTERS[stateFilter](m) && (!q || modelMatches(m, q)));
  const clearFilters = () => { setSearch(''); setStateFilter('all'); };

  const testModel = testKey ? models.find((m) => m.model_key === testKey) : null;
  const testState = testKey ? getTestState(testKey) : null;

  const formats = registry?.formats || [];
  const currentFormat = formats.find((f) => f.value === formData.api_type) || null;
  const formatHint = currentFormat
    ? currentFormat.description
    : (registry ? 'This format is not one the proxy can serve: pick one of the list.'
      : 'The format list did not load; reload the page before saving.');
  const modes = currentFormat?.reasoning || [];
  const defaults = registry?.defaults || {};
  const takes = (key) => (currentFormat?.settings || []).includes(key);
  const showField = (key) => takes(key) && (MODE_FIELDS[formData.reasoning] || []).includes(key);
  // A mode can have levels of its own (GLM's low / high / max), so the list and the default
  // come from the mode, and a level saved under another mode shows as the default.
  const efforts = effortsFor(currentFormat, formData.reasoning) || registry?.efforts || [];
  const effortDefault = modes.find((m) => m.value === formData.reasoning)?.effort_default
    || defaults.effort || '';
  const effortValue = efforts.includes(formData.effort) ? formData.effort
    : (efforts.includes(effortDefault) ? effortDefault : efforts[0] || '');

  // A format change keeps only a reasoning mode the new format supports.
  const changeFormat = (value) => setFormData((f) => {
    const next = formats.find((x) => x.value === value);
    const allowed = (next?.reasoning || []).map((m) => m.value);
    return { ...f, api_type: value, reasoning: allowed.includes(f.reasoning) ? f.reasoning : (f.supports_reasoning ? allowed[0] || '' : '') };
  });
  const toggleReasoning = (on) => setFormData((f) => ({
    ...f, supports_reasoning: on, reasoning: on ? (f.reasoning || modes[0]?.value || '') : '',
  }));

  const keyHint = editingKey && formData._api_key_set ? (
    <>
      Current key <code>{formData._api_key_display}</code>
      {formData._api_key_updated_at && `, updated ${formatDate(formData._api_key_updated_at)}`}.
      Leave blank to keep it.
    </>
  ) : 'Write-only: after saving, only a masked preview is shown.';

  const addButton = (
    <button type="button" className="btn btn-primary" onClick={openCreate}>
      <Icon name="plus" /> Add model
    </button>
  );

  /* ── Render ────────────────────────────────────────────────── */
  return (
    <div>
      {/* This page had no heading at all: it opened on a card called "Platform
          Models", so the only thing naming it was the sidebar. The title and the
          primary action live in the page head, and no card header repeats the name
          one box in. */}
      <PageHeader
        description="The LLMs tiers can be pointed at. Each row carries its own endpoint and key."
        actions={addButton}
      >
        <h1 className="page-title">
          Models
          {!loading && <Badge tone="neutral" size="lg">{models.length}</Badge>}
        </h1>
      </PageHeader>

      <div className="card card-flush">
        <div className="toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search name, key, model ID, endpoint…"
            label="Search models"
          />
          <Tabs
            size="sm"
            label="Filter models by state"
            value={stateFilter}
            onChange={setStateFilter}
            tabs={[
              { value: 'all', label: 'All', count: models.length },
              { value: 'active', label: 'Active', count: activeCount },
              { value: 'inactive', label: 'Inactive', count: models.length - activeCount },
            ]}
          />
        </div>

        {loading ? (
          <TableSkeleton rows={5} cols={8} label="Loading models" />
        ) : models.length === 0 && loadError ? (
          <EmptyState
            icon="alertCircle"
            title="Could not load models"
            action={<button type="button" className="btn btn-secondary" onClick={retryLoad}>Retry</button>}
          >
            The model list did not load. Nothing has been changed.
          </EmptyState>
        ) : models.length === 0 ? (
          <EmptyState icon="models" title="No models yet" action={addButton}>
            Tiers and tools can only use a model that is configured here.
          </EmptyState>
        ) : visible.length === 0 ? (
          <EmptyState
            icon="search"
            title={q ? `No models match “${search.trim()}”` : `No ${stateFilter} models`}
            action={<button type="button" className="btn btn-secondary" onClick={clearFilters}>Clear filters</button>}
          >
            Search covers name, key, model ID, format and endpoint.
          </EmptyState>
        ) : (
          <div className="table-container">
            <table className="table table-stack">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>Format</th>
                  <th>Upstream</th>
                  <th className="num">Context</th>
                  <th className="num">Max output</th>
                  <th className="num">Input / 1M</th>
                  <th className="num">Output / 1M</th>
                  <th>Status</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((model) => {
                  const test = getTestState(model.model_key);
                  const mode = formats.find((f) => f.value === model.api_type)?.reasoning
                    .find((m) => m.value === model.settings?.reasoning);
                  return (
                    <tr key={model.model_key}>
                      <td data-label="Model">
                        {/* max-width on a <td> is ignored by table layout; on a block
                            inside it, it holds, so a long key truncates instead of
                            widening the column. */}
                        <div style={{ maxWidth: 220, minWidth: 0 }}>
                          <span className="cell-primary">{model.ui_name}</span>
                          <span className="cell-sub mono truncate" title={model.model_key}>{model.model_key}</span>
                          {/* Same upstream model and prices as an older row, so usage and
                              cost are recorded under that row (llm_formats.report_keys). */}
                          {model.reported_as && (
                            <span className="cell-sub text-xs text-muted">
                              Usage shown as {model.reported_as_name || model.reported_as}
                            </span>
                          )}
                        </div>
                      </td>
                      <td data-label="Format">
                        <div className="stack gap-1" style={{ alignItems: 'flex-start' }}>
                          {providerBadge(model.api_type) || DASH}
                          {model.api_type && (
                            // Allowed to wrap at its hyphen: nine columns have to fit a
                            // 1440px screen, and this was the widest unbreakable one.
                            <span className="mono text-xs text-muted">
                              {model.api_type}
                            </span>
                          )}
                        </div>
                      </td>
                      <td data-label="Upstream">
                        <div style={{ maxWidth: 200, minWidth: 0 }}>
                          <div className="mono text-sm truncate" title={model.model_id}>{model.model_id}</div>
                          <span className="cell-sub truncate" title={model.base_url || undefined}>
                            {shortUrl(model.base_url) || 'Endpoint not set'}
                            {model.namespace_id ? ` · ns ${model.namespace_id}` : ''}
                          </span>
                        </div>
                      </td>
                      <td data-label="Context" className="num">{formatTokens(model.context_window) || DASH}</td>
                      <td data-label="Max output" className="num">{formatTokens(model.max_tokens) || DASH}</td>
                      <td data-label="Input / 1M" className="num">{formatPrice(model.price_input_per_m)}</td>
                      <td data-label="Output / 1M" className="num">{formatPrice(model.price_output_per_m)}</td>
                      <td data-label="Status">
                        <div className="stack gap-1" style={{ alignItems: 'flex-start' }}>
                          <div className="status-lines">
                            <span className={model.active ? 'text-ok' : 'text-muted'}>
                              <span className="dot" aria-hidden="true" />{model.active ? 'Active' : 'Inactive'}
                            </span>
                            {!model.api_key_set && (
                              <span className="text-warn">
                                <span className="dot" aria-hidden="true" />No API key
                              </span>
                            )}
                          </div>
                          {(model.supports_reasoning || model.available_as_bg) && (
                            <div className="row-wrap gap-1">
                              {model.supports_reasoning && (
                                <Badge tone="info" title={mode ? `Reasoning: ${mode.label}` : undefined}>
                                  Reasoning{mode && model.settings?.reasoning !== 'model_default' ? `: ${mode.label}` : ''}
                                </Badge>
                              )}
                              {model.available_as_bg && <Badge tone="outline">Background</Badge>}
                            </div>
                          )}
                        </div>
                      </td>
                      <td data-label="Actions">
                        <div className="row gap-1 row-end">
                          <button type="button" className="btn btn-secondary btn-sm"
                                  onClick={() => setTestKey(model.model_key)}
                                  aria-label={`Test ${model.ui_name}`}>
                            {test.loading ? <span className="spinner-sm" /> : <Icon name="play" />} Test
                          </button>
                          <button type="button" className="btn btn-ghost btn-sm btn-icon"
                                  onClick={() => handleEdit(model)}
                                  aria-label={`Edit ${model.ui_name}`} title="Edit">
                            <Icon name="edit" />
                          </button>
                          <button type="button" className="btn btn-danger-soft btn-sm btn-icon"
                                  onClick={() => handleDelete(model.model_key)}
                                  aria-label={`Delete ${model.ui_name}`} title="Delete model">
                            <Icon name="trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit Form */}
      <Modal
        open={showForm}
        onClose={() => { if (!saving) resetForm(); }}
        title={editingKey ? `Edit ${formData.ui_name || editingKey}` : 'Add model'}
        description={editingKey
          ? 'Changes apply wherever this model is used.'
          : 'Each model carries its own endpoint and key.'}
        maxWidth={760}
      >
        <form onSubmit={handleSubmit}>
          <section aria-labelledby="model-form-identity">
            <h2 id="model-form-identity" className="section-title">Identity</h2>
            <div className="form-row">
              <Field label="Display name" htmlFor="model-ui-name" required>
                <input id="model-ui-name" className="form-input" required value={formData.ui_name}
                       onChange={(e) => update('ui_name', e.target.value)} placeholder="Claude Sonnet 4.6" />
              </Field>
              <Field
                label="Model key"
                htmlFor="model-key"
                required
                hint={editingKey
                  ? 'Tiers and tool settings refer to the model by this key, so it cannot change.'
                  : 'Lowercase, spaces and slashes become dashes. Tiers and tool settings refer to the model by it.'}
              >
                {/* No slash: the console addresses a model by its key in the URL, and a key
                    with one could not be tested, edited or deleted. The API refuses it too. */}
                <input id="model-key" className="form-input mono" required value={formData.model_key}
                       disabled={!!editingKey} spellCheck={false} autoComplete="off"
                       onChange={(e) => update('model_key', e.target.value.toLowerCase().replace(/[\s/]/g, '-'))}
                       placeholder="claude-sonnet-4-6" />
              </Field>
            </div>
            <Field label="Format" htmlFor="model-format" required hint={formatHint}>
              <select id="model-format" className="form-input" value={formData.api_type}
                      onChange={(e) => changeFormat(e.target.value)}>
                {/* A value the registry does not know still shows as itself rather than as
                    the first option. */}
                {formData.api_type && !currentFormat && (
                  <option value={formData.api_type}>{formData.api_type} (not a current format)</option>
                )}
                {formats.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
            </Field>
            <Field label="Model ID" htmlFor="model-id" required
                   hint="The exact identifier sent in the request body to the upstream.">
              <input id="model-id" className="form-input mono" required value={formData.model_id}
                     spellCheck={false} onChange={(e) => update('model_id', e.target.value)}
                     placeholder="Qwen/Qwen3.6-35B-A3B-FP8" />
            </Field>
          </section>

          <section aria-labelledby="model-form-endpoint" className="mt-4">
            <h2 id="model-form-endpoint" className="section-title">Endpoint &amp; credentials</h2>
            <Field label="Endpoint URL" htmlFor="model-base-url" required>
              <input id="model-base-url" className="form-input mono" value={formData.base_url || ''}
                     inputMode="url" spellCheck={false} autoComplete="off"
                     onChange={(e) => update('base_url', e.target.value)}
                     placeholder={currentFormat?.base_url_hint || 'https://host/v1/chat/completions'} />
            </Field>
            <div className="form-row">
              {(currentFormat?.fields || []).includes('namespace_id') && (
                <Field label="Namespace" htmlFor="model-namespace" hint="Fastweb vLLM gateway only.">
                  <input id="model-namespace" className="form-input mono" value={formData.namespace_id || ''}
                         spellCheck={false} onChange={(e) => update('namespace_id', e.target.value)}
                         placeholder="coe-vllm-qwen-35b" />
                </Field>
              )}
              {takes('api_version') && (
                <Field label="API version" htmlFor="model-api-version"
                       hint={`Azure's api-version query parameter. Blank means ${defaults.api_version || 'the default'}.`}>
                  <input id="model-api-version" className="form-input mono" value={formData.api_version || ''}
                         spellCheck={false} onChange={(e) => update('api_version', e.target.value)}
                         placeholder={defaults.api_version || '2024-10-21'} />
                </Field>
              )}
              <Field
                label="API key"
                htmlFor="model-api-key"
                hint={keyHint}
                extra={editingKey && (formData._api_key_set
                  ? <Badge tone="ok" dot>Configured</Badge>
                  : <Badge tone="warn" dot>Missing</Badge>)}
              >
                {/* new-password, not off: browsers ignore "off" on a password field and
                    may offer the admin's own saved login, which would then be sent as
                    this model's key. */}
                <input id="model-api-key" className="form-input" type="password" autoComplete="new-password"
                       spellCheck={false} value={formData.api_key || ''}
                       onChange={(e) => update('api_key', e.target.value)}
                       placeholder={editingKey
                         ? (formData._api_key_set ? 'Leave blank to keep current key' : 'Enter API key…')
                         : 'Enter API key…'} />
              </Field>
            </div>
            {takes('send_stream_options') && (
              <SwitchRow
                id="model-stream-options"
                title="Ask for usage when streaming"
                description="Sends stream_options.include_usage, which most hosts need to count the tokens of a streamed reply. Off only for a host that adds it itself and refuses it twice, as the AI Factory's DeepSeek V4.1 does."
                checked={formData.send_stream_options}
                onChange={(v) => update('send_stream_options', v)}
              />
            )}
          </section>

          <section aria-labelledby="model-form-limits" className="mt-4">
            <h2 id="model-form-limits" className="section-title">Limits</h2>
            <div className="form-row cols-3">
              <Field label="Context window" htmlFor="model-context" hint="Tokens.">
                <input id="model-context" className="form-input" type="number" value={formData.context_window}
                       onChange={(e) => update('context_window', e.target.value)} placeholder="200000" />
              </Field>
              <Field label="Max output tokens" htmlFor="model-max-tokens">
                <input id="model-max-tokens" className="form-input" type="number" value={formData.max_tokens}
                       onChange={(e) => update('max_tokens', e.target.value)} placeholder="16384" />
              </Field>
              <Field label="Sort order" htmlFor="model-sort" hint="Lower comes first.">
                <input id="model-sort" className="form-input" type="number" value={formData.sort_order}
                       onChange={(e) => update('sort_order', e.target.value)} />
              </Field>
            </div>
          </section>

          <section aria-labelledby="model-form-reasoning" className="mt-4">
            <h2 id="model-form-reasoning" className="section-title">Reasoning</h2>
            <SwitchRow
              id="model-reasoning"
              title="Supports reasoning"
              description="The model produces a reasoning trace. Off, the proxy asks for none."
              checked={formData.supports_reasoning}
              onChange={toggleReasoning}
            />
            {formData.supports_reasoning && (
              <div className="form-row cols-3 mt-2">
                <Field label="How the proxy steers it" htmlFor="model-reasoning-mode" required
                       hint={formData.reasoning === 'chat_template'
                         ? 'On for conversation, off for captions, proofreading and the empty-reply retry.'
                         : formData.reasoning === 'no_thinking'
                           ? 'Off for every caller: for a model used only for short tasks such as captions.'
                         : formData.reasoning === 'template_effort'
                           ? 'For GLM 5, which always thinks: low, high or max. Captions, proofreading and the empty-reply retry use low.'
                         : formData.reasoning === 'model_default'
                           ? 'Nothing is sent; the model decides. An empty reply is not retried, because the proxy does not know how to quieten this model.'
                         : formData.reasoning === 'effort' && formData.api_type === 'bedrock-converse'
                           ? 'Claude 5.5 and later, which think adaptively. Temperature is not sent.'
                           : formData.reasoning === 'extended_thinking' ? 'Claude up to 4.6.' : undefined}>
                  <select id="model-reasoning-mode" className="form-input" value={formData.reasoning}
                          onChange={(e) => update('reasoning', e.target.value)}>
                    {!modes.some((m) => m.value === formData.reasoning) && <option value="">Pick a mode</option>}
                    {modes.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </Field>
                {showField('effort') && (
                  <Field label="Effort" htmlFor="model-effort">
                    <select id="model-effort" className="form-input" value={effortValue}
                            onChange={(e) => update('effort', e.target.value)}>
                      {efforts.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </Field>
                )}
                {showField('budget_tokens') && (
                  <Field label="Thinking budget" htmlFor="model-budget" hint="Tokens, at least 1024.">
                    <input id="model-budget" className="form-input" type="number" min="1024"
                           value={formData.budget_tokens} placeholder={String(defaults.budget_tokens || 8000)}
                           onChange={(e) => update('budget_tokens', e.target.value)} />
                  </Field>
                )}
                {showField('min_output_tokens') && (
                  <Field label="Output floor" htmlFor="model-min-output"
                         hint="The trace and the answer share one budget; max tokens is raised to this.">
                    <input id="model-min-output" className="form-input" type="number" min="256"
                           value={formData.min_output_tokens} placeholder={String(defaults.min_output_tokens || 16384)}
                           onChange={(e) => update('min_output_tokens', e.target.value)} />
                  </Field>
                )}
              </div>
            )}
          </section>

          <section aria-labelledby="model-form-pricing" className="mt-4">
            <h2 id="model-form-pricing" className="section-title">
              Pricing <span className="text-sm text-muted fw-400">EUR per 1M tokens</span>
            </h2>
            <div className="form-row cols-3">
              {PRICE_FIELDS.map(([key, label, hint, placeholder]) => (
                <Field key={key} label={label} htmlFor={`model-${key}`} hint={hint}>
                  <input id={`model-${key}`} className="form-input" type="number" step="0.01" min="0"
                         value={formData[key]} onChange={(e) => update(key, e.target.value)}
                         placeholder={placeholder || '0.00'} />
                </Field>
              ))}
            </div>
          </section>

          <section aria-labelledby="model-form-availability" className="mt-4">
            <h2 id="model-form-availability" className="section-title">Availability</h2>
            <SwitchRow
              id="model-active"
              title="Active"
              description="Inactive models are left out of the tier and Tools model pickers."
              checked={formData.active}
              onChange={(v) => update('active', v)}
            />
            <SwitchRow
              id="model-background"
              title="Available as background"
              checked={formData.available_as_bg}
              onChange={(v) => update('available_as_bg', v)}
            />
          </section>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={resetForm} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving
                ? <><span className="spinner-sm" /> Saving…</>
                : (editingKey ? 'Save model' : 'Add model')}
            </button>
          </div>
        </form>
      </Modal>

      {/* Connection test */}
      <Modal
        open={!!testModel}
        onClose={() => setTestKey(null)}
        title={testModel ? `Test ${testModel.ui_name}` : ''}
        description="Sends one prompt through proxy-llm as an agent call, with this model's settings, and shows what it sent and what came back. Nothing is billed."
        maxWidth={640}
      >
        {testModel && (
          <>
            <dl className="kv mb-4">
              <dt>Model ID</dt>
              <dd className="mono">{testModel.model_id}</dd>
              <dt>Endpoint</dt>
              <dd className="mono">{testModel.base_url || 'Not set'}</dd>
              <dt>Format</dt>
              <dd className="row-wrap gap-2">
                {providerBadge(testModel.api_type)}
                <span className="mono text-sm text-muted">{testModel.api_type}</span>
              </dd>
            </dl>
            <form onSubmit={(e) => { e.preventDefault(); if (!testState.loading) runTest(testKey); }}>
              <div className="row-wrap gap-3 mb-3">
                <Tabs
                  size="sm"
                  label="Call the model as"
                  value={testState.mode || 'conversation'}
                  onChange={(v) => setTestMode(testKey, v)}
                  tabs={CALL_MODES}
                />
                <span className="text-sm text-muted">
                  {CALL_MODE_HINTS[testState.mode || 'conversation']}
                </span>
              </div>
              <label className="form-label" htmlFor="model-test-prompt">Prompt</label>
              <div className="input-group">
                <input id="model-test-prompt" className="form-input" value={testState.prompt}
                       onChange={(e) => setTestPrompt(testKey, e.target.value)} placeholder="Test prompt…" />
                <button type="submit" className="btn btn-primary" disabled={testState.loading}>
                  {testState.loading
                    ? <><span className="spinner-sm" /> Testing…</>
                    : <><Icon name="play" /> Run test</>}
                </button>
              </div>
            </form>
            <div className="mt-4" aria-live="polite">
              {testState.result && <TestResult result={testState.result} />}
            </div>
          </>
        )}
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setTestKey(null)}>Close</button>
        </div>
      </Modal>

      {confirmDialog}
    </div>
  );
}

export default Models;
